/**
 * DemocratCharts — theme state.
 *
 * Tracks light vs dark theme. The actual visual swap happens via CSS variables
 * defined in index.css under `.dark` and `.light` selectors on `<html>`. We
 * apply the matching class here and persist the choice.
 *
 * Note: main.tsx applies the class before React renders to prevent FOUC,
 * so this store is in sync with the DOM by the time it first mounts.
 */

import { create } from "zustand";

export type Theme = "light" | "dark";

const LS_KEY = "livecharts:theme";

function loadTheme(): Theme {
  try {
    const t = localStorage.getItem(LS_KEY);
    if (t === "light" || t === "dark") return t;
  } catch {
    /* ignore */
  }
  return "dark";
}

function applyToDOM(theme: Theme) {
  const el = document.documentElement;
  el.classList.remove("light", "dark");
  el.classList.add(theme);
}

function persist(theme: Theme) {
  try {
    localStorage.setItem(LS_KEY, theme);
  } catch {
    /* ignore */
  }
}

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: loadTheme(),
  setTheme: (t) => {
    persist(t);
    applyToDOM(t);
    set({ theme: t });
  },
  toggle: () => {
    const next: Theme = get().theme === "dark" ? "light" : "dark";
    persist(next);
    applyToDOM(next);
    set({ theme: next });
  },
}));

/**
 * Read theme-aware chart colors from the live CSS variables. Useful for
 * libraries like lightweight-charts that don't consume Tailwind directly.
 *
 * IMPORTANT: lightweight-charts has its own color parser that only accepts
 * the legacy comma-separated rgb()/rgba() syntax — NOT the modern
 * space-separated form. We convert the CSS variable triplet (e.g. "120 123
 * 134") into "120, 123, 134" here.
 */
export function readChartColors() {
  const style = getComputedStyle(document.documentElement);
  const triplet = (name: string) => {
    const raw = style.getPropertyValue(`--${name}`).trim();
    return raw.split(/\s+/).join(", ");
  };
  const rgb  = (name: string) => `rgb(${triplet(name)})`;
  const rgba = (name: string, alpha: number) => `rgba(${triplet(name)}, ${alpha})`;
  return {
    background: rgb("chart-bg"),
    text:       rgb("chart-text"),
    grid:       rgb("chart-grid"),
    border:     rgb("chart-border"),
    crosshair:  rgb("chart-crosshair"),
    bull:       rgb("bull"),
    bear:       rgb("bear"),
    accent:     rgb("accent"),
    bullAlpha: (a: number) => rgba("bull", a),
    bearAlpha: (a: number) => rgba("bear", a),
  };
}
